import algoliasearch from 'algoliasearch';
import { useConfigStore } from '~/store/config';

interface RecommendParams {
  objectIDs?: string[];
  maxRecommendations?: number;
  queryParameters?: {
    filters?: string;
    facetFilters?: string[];
    numericFilters?: string[];
  };
  fallbackParameters?: {
    filters?: string;
    facetFilters?: string[];
  };
  facetName?: string;
  facetValue?: string;
}

export default function useAlgoliaRecommend() {
  const config = useConfigStore();
  const runtimeConfig = useRuntimeConfig();

  const client = algoliasearch(
    runtimeConfig.public.algoliaId,
    runtimeConfig.public.algoliaSecret
  );

  /**
   * Get related products based on a product objectID
   */
  const getRelatedProducts = async(
    objectID: string,
    params?: RecommendParams
  ) => {
    const indexName = `${config.algoliaBaseIndex}_products`;
    
    try {
      const { results } = await client.getRecommendations([
        {
          indexName,
          objectID,
          model: 'related-products',
          maxRecommendations: params?.maxRecommendations || 8,
          queryParameters: params?.queryParameters,
          fallbackParameters: params?.fallbackParameters,
        },
      ]);
      
      return results[0]?.hits || [];
    } catch (error) {
      console.error('Error fetching related products:', error);
      return [];
    }
  };

  /**
   * Get frequently bought together products
   */
  const getFrequentlyBoughtTogether = async(
    objectID: string,
    params?: RecommendParams
  ) => {
    const indexName = `${config.algoliaBaseIndex}_products`;
    
    try {
      const { results } = await client.getRecommendations([
        {
          indexName,
          objectID,
          model: 'bought-together',
          maxRecommendations: params?.maxRecommendations || 4,
          queryParameters: params?.queryParameters,
          fallbackParameters: params?.fallbackParameters,
        },
      ]);
      
      return results[0]?.hits || [];
    } catch (error) {
      console.error('Error fetching frequently bought together:', error);
      return [];
    }
  };

  /**
   * Get trending items (global or per facet)
   */
  const getTrendingItems = async(params?: RecommendParams) => {
    const indexName = `${config.algoliaBaseIndex}_products`;
    
    try {
      const recommendRequest: {
        indexName: string;
        model: string;
        maxRecommendations: number;
        queryParameters?: RecommendParams['queryParameters'];
        fallbackParameters?: RecommendParams['fallbackParameters'];
        facetName?: string;
        facetValue?: string;
      } = {
        indexName,
        model: 'trending-items',
        maxRecommendations: params?.maxRecommendations || 8,
        queryParameters: params?.queryParameters,
        fallbackParameters: params?.fallbackParameters,
      };

      // If facet filtering is needed
      if (params?.facetName && params?.facetValue) {
        recommendRequest.facetName = params.facetName;
        recommendRequest.facetValue = params.facetValue;
      }

      const { results } = await client.getRecommendations([recommendRequest]);
      
      return results[0]?.hits || [];
    } catch (error) {
      console.error('Error fetching trending items:', error);
      return [];
    }
  };

  /**
   * Get looking similar products (based on images)
   */
  const getLookingSimilar = async(
    objectID: string,
    params?: RecommendParams
  ) => {
    const indexName = `${config.algoliaBaseIndex}_products`;
    
    try {
      const { results } = await client.getRecommendations([
        {
          indexName,
          objectID,
          model: 'looking-similar',
          maxRecommendations: params?.maxRecommendations || 8,
          queryParameters: params?.queryParameters,
        },
      ]);
      
      return results[0]?.hits || [];
    } catch (error) {
      console.error('Error fetching looking similar:', error);
      return [];
    }
  };

  return {
    getRelatedProducts,
    getFrequentlyBoughtTogether,
    getTrendingItems,
    getLookingSimilar,
  };
}
